// The Bible reader: a chapter at a time, verse numbers in the margin of the line, in the translation's
// own language and direction. Books and chapters from the contents; the translation from the list of
// every Bible in the library. Select words to highlight them or write a note; they sync. The place is
// remembered, so /bible/:id opens here next time, on any device.
//
// The chrome (src/reader/ReaderBar.tsx) is a quiet bar that steps away while you read; the column
// itself never moves. The arrow keys turn to the previous or next chapter, as a page turns: no more.
//
// A package Bible is read from its .bslib with ranged reads (SQLite in a worker, src/reader/sqlite);
// only the pages of this chapter are fetched. The WEB is read from public/bible/web.
import { createFileRoute, Link, useNavigate } from "@tanstack/react-router";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button, LinkButton } from "../../../components/Button";
import { Sheet } from "../../../components/Dialog";
import { Empty } from "../../../components/Empty";
import { Icon } from "../../../components/Icon";
import { SyncIndicator } from "../../../components/SyncIndicator";
import ui from "../../../components/ui.module.css";
import { bookByOsis } from "../../../data/books";
import { LANGUAGE, UNAVAILABLE_NOTE, useLibraryEntries, useLibraryEntry, type LibraryEntry } from "../../../data/catalogueSource";
import { useTitle } from "../../../lib/useTitle";
import { useBibleChapter, useBibleIndex } from "../../../reader/bible";
import { ChapterLoading, ChapterText } from "../../../reader/ChapterText";
import { packageProblem } from "../../../reader/problems";
import type { BibleBook, BibleIndex } from "../../../reader/packages";
import { BookmarkButton, ReaderBar, Ribbon, useBookmark } from "../../../reader/ReaderBar";
import { MarksList, ReaderTools } from "../../../reader/ReaderTools";
import { usePageTurns } from "../../../reader/usePageTurns";
import { useMarks, useRememberPlace } from "../../../reader/useMarks";
import { verseSelectionSubject } from "../../../share/subjects";
import s from "./bible.module.css";

export const Route = createFileRoute("/_app/bible/$id_/$book/$chapter")({ component: BibleReader });

function BibleReader() {
  const { id, book, chapter: ch } = Route.useParams();
  const chapter = Number(ch);
  const { data: entry, isPending, isError } = useLibraryEntry(id);
  const readable = entry?.kind === "bible" && entry.available ? entry : null;
  const index = useBibleIndex(readable);
  const verses = useBibleChapter(readable, index.data, book, chapter);
  const b = index.data?.books.find((x) => x.key === book);
  const label = b ? `${bookByOsis(b.key)?.ref ?? b.name} ${chapter}` : `${book} ${ch}`;
  useTitle(entry ? `${label} · ${entry.shortTitle}` : "Bible");

  if (isPending) return <div className={s.page} aria-busy="true" aria-label="Opening the Bible" />;
  if (isError || !entry || entry.kind !== "bible")
    return (
      <div className={ui.page}>
        <Empty title="This Bible isn’t in the library.">
          <LinkButton to="/library" icon="back">
            Library
          </LinkButton>
        </Empty>
      </div>
    );
  if (!entry.available)
    return (
      <div className={ui.page}>
        <Empty title={`${entry.title} isn’t available yet.`}>
          <p className={s.why}>{UNAVAILABLE_NOTE}</p>
          <LinkButton to="/library">Choose another Bible</LinkButton>
        </Empty>
      </div>
    );
  const problem = index.error ?? verses.error;
  if (problem) {
    const p = packageProblem(problem);
    return (
      <div className={ui.page}>
        <Empty title={p.title}>
          {p.detail && <p className={s.why}>{p.detail}</p>}
          <Button onClick={() => (index.error ? void index.refetch() : void verses.refetch())}>Try again</Button>
        </Empty>
      </div>
    );
  }
  if (index.data && (!b || !b.chapters.includes(chapter)))
    return (
      <div className={ui.page}>
        <Empty title={`${label} isn’t in ${entry.shortTitle}.`}>
          <LinkButton to="/bible/$id" params={{ id }}>
            {`Open ${entry.shortTitle}`}
          </LinkButton>
        </Empty>
      </div>
    );
  return <Chapter key={`${id}/${book}/${chapter}`} entry={entry} index={index.data} book={b} chapter={chapter} label={label} verses={verses.data} />;
}

function Chapter({
  entry,
  index,
  book,
  chapter,
  label,
  verses,
}: {
  entry: LibraryEntry;
  index: BibleIndex | undefined;
  book: BibleBook | undefined;
  chapter: number;
  label: string;
  verses: ReturnType<typeof useBibleChapter>["data"];
}) {
  const article = useRef<HTMLElement>(null);
  const [contents, setContents] = useState(false);
  const { highlights, notes } = useMarks(entry.id);
  const remember = useRememberPlace();
  const navigate = useNavigate();
  const key = book?.key ?? "";
  const bookmark = useBookmark(entry.id, { verseKey: `${key}.${chapter}.1` }, label);

  // Remember the place once the chapter is shown: the engine folds repeated moves into one change.
  useEffect(() => {
    if (!verses?.length || !index || !book) return;
    const all = index.books.flatMap((x) => x.chapters.map((c) => `${x.key}.${c}`));
    const at = all.indexOf(`${book.key}.${chapter}`);
    void remember(entry.id, { verseKey: `${book.key}.${chapter}.${verses[0]!.n}` }, all.length ? at / all.length : null);
  }, [verses, index, book, chapter, entry.id, remember]);

  const nav = useMemo(() => {
    if (!index || !book) return { prev: null, next: null };
    const all = index.books.flatMap((x) => x.chapters.map((c) => ({ book: x, chapter: c })));
    const at = all.findIndex((x) => x.book.key === book.key && x.chapter === chapter);
    return { prev: all[at - 1] ?? null, next: all[at + 1] ?? null };
  }, [index, book, chapter]);

  const go = (to: { book: BibleBook; chapter: number } | null) =>
    to && void navigate({ to: "/bible/$id/$book/$chapter", params: { id: entry.id, book: to.book.key, chapter: String(to.chapter) } });
  usePageTurns(
    () => go(nav.prev),
    () => go(nav.next),
  );

  const inChapter = (vk: string) => vk.startsWith(`${key}.${chapter}.`);
  const chapterHighlights = highlights.filter((h) => (h.anchor.verseKey && inChapter(h.anchor.verseKey)) || (h.anchor.endVerseKey && inChapter(h.anchor.endVerseKey)));
  const chapterNotes = notes.filter((n) => n.anchor?.verseKey && (inChapter(n.anchor.verseKey) || (n.anchor.endVerseKey && inChapter(n.anchor.endVerseKey))));
  const verseLabel = (a: { verseKey?: string; endVerseKey?: string }) => {
    const v = (k?: string) => k?.split(".").pop();
    return a.endVerseKey && a.endVerseKey !== a.verseKey ? `verses ${v(a.verseKey)}-${v(a.endVerseKey)}` : `verse ${v(a.verseKey)}`;
  };
  const textOf = (vk?: string) => {
    const n = Number(vk?.split(".").pop());
    const v = verses?.find((x) => x.n === n);
    return v
      ? v.blocks
          .flatMap((b) => b.runs ?? [])
          .map((r) => (typeof r === "string" ? r : (r.text ?? "")))
          .join("")
      : null;
  };
  const items = [
    ...chapterHighlights.map((h) => ({
      id: h.id,
      kind: "highlight" as const,
      label: verseLabel(h.anchor),
      color: h.color,
      text: h.comment ?? textOf(h.anchor.verseKey),
      highlight: h,
    })),
    ...chapterNotes.map((n) => ({ id: n.id, kind: "note" as const, label: verseLabel(n.anchor!), text: n.body, note: n })),
  ];
  const ref = bookByOsis(key)?.ref;
  const name = (b: BibleBook) => bookByOsis(b.key)?.ref ?? b.name;

  return (
    <div className={s.page}>
      <a className="skip-link" href="#text">
        Skip to the text
      </a>
      <ReaderBar
        head={`${label} · ${entry.shortTitle}`}
        start={
          <>
            <Link to="/library" className={ui.back} aria-label="Library">
              <Icon name="back" />
            </Link>
            <button type="button" className={s.place} onClick={() => setContents(true)} aria-haspopup="dialog">
              <Icon name="contents" size={18} />
              Books
            </button>
            <TranslationPicker entry={entry} book={key} chapter={chapter} />
          </>
        }
        end={
          <>
            <BookmarkButton {...bookmark} label={label} />
            <SyncIndicator compact />
          </>
        }
      />

      <header className={s.head}>
        <Ribbon kept={bookmark.kept} />
        <h1 id="chapter-title" className={s.title}>
          {label}
        </h1>
        <p className={s.from}>
          {entry.title}
          {book?.display ? <span lang={index?.language ?? undefined}> · {book.display}</span> : null}
        </p>
      </header>

      {!verses ? (
        <ChapterLoading label={label} />
      ) : (
        <ChapterText
          id="text"
          articleRef={article}
          verses={verses}
          book={key}
          chapter={chapter}
          label={label}
          highlights={highlights}
          notes={notes}
          language={index?.language}
          direction={index?.direction}
          labelledBy="chapter-title"
        />
      )}

      <nav className={s.pager} aria-label="Chapters">
        {nav.prev ? (
          <Link to="/bible/$id/$book/$chapter" params={{ id: entry.id, book: nav.prev.book.key, chapter: String(nav.prev.chapter) }} className={s.prev}>
            <span className={s.pagerLabel}>
              <Icon name="back" size={16} />
              Previous
            </span>
            <span className={s.pagerTitle}>
              {name(nav.prev.book)} {nav.prev.chapter}
            </span>
          </Link>
        ) : (
          <span />
        )}
        {nav.next && (
          <Link to="/bible/$id/$book/$chapter" params={{ id: entry.id, book: nav.next.book.key, chapter: String(nav.next.chapter) }} className={s.next}>
            <span className={s.pagerLabel}>
              Next
              <Icon name="forward" size={16} />
            </span>
            <span className={s.pagerTitle}>
              {name(nav.next.book)} {nav.next.chapter}
            </span>
          </Link>
        )}
      </nav>

      <MarksList title="Your highlights and notes" items={items} />

      <p className={s.licence}>{[entry.title, index?.licence, index?.source ? `Source: ${index.source}` : null].filter(Boolean).join(". ")}.</p>

      <ReaderTools
        libraryId={entry.id}
        root={article}
        kind="verse"
        highlights={highlights}
        notes={notes}
        shareHref={(sel) => {
          if (!ref || !sel.anchor.verseKey) return null;
          const v = (k?: string) => k?.split(".").pop();
          const end = sel.anchor.endVerseKey ? `-${v(sel.anchor.endVerseKey)}` : "";
          return `/new?kind=verse&ref=${encodeURIComponent(`${ref} ${chapter}:${v(sel.anchor.verseKey)}${end}`)}`;
        }}
        quoteCard={(sel) => verseSelectionSubject(sel, label, entry.shortTitle)}
      />

      {index && contents && <Contents open entry={entry} index={index} current={{ book: key, chapter }} onClose={() => setContents(false)} />}
    </div>
  );
}

/**
 * Every Bible in the library, by language: the same chapter in another translation. The native select
 * does the choosing (it is the best list on every device); over it, a pill shows the short name.
 */
function TranslationPicker({ entry, book, chapter }: { entry: LibraryEntry; book: string; chapter: number }) {
  const { data: all = [] } = useLibraryEntries();
  const navigate = useNavigate();
  const bibles = all.filter((e) => e.kind === "bible" && e.available);
  const groups = new Map<string, LibraryEntry[]>();
  for (const e of bibles) {
    const lang = LANGUAGE[e.language ?? ""] ?? e.language ?? "Other";
    groups.set(lang, [...(groups.get(lang) ?? []), e]);
  }
  return (
    <label className={s.picker}>
      <span className="visually-hidden">Translation</span>
      <span className={s.pickerFace} aria-hidden="true">
        {entry.shortTitle}
        <Icon name="caret" size={12} />
      </span>
      <select value={entry.id} onChange={(e) => void navigate({ to: "/bible/$id/$book/$chapter", params: { id: e.target.value, book, chapter: String(chapter) } })}>
        {[...groups.entries()]
          .sort(([a], [b]) => (a === "English" ? -1 : b === "English" ? 1 : a.localeCompare(b)))
          .map(([lang, list]) => (
            <optgroup key={lang} label={lang}>
              {list.map((e) => (
                <option key={e.id} value={e.id}>
                  {e.shortTitle}, {e.title}
                </option>
              ))}
            </optgroup>
          ))}
        {!bibles.some((b) => b.id === entry.id) && <option value={entry.id}>{entry.shortTitle}</option>}
      </select>
    </label>
  );
}

/** The navigator: the books in canonical order, each opening to its chapters as a grid of numbers. */
function Contents({ open, entry, index, current, onClose }: { open: boolean; entry: LibraryEntry; index: BibleIndex; current: { book: string; chapter: number }; onClose: () => void }) {
  const [shown, setShown] = useState(current.book);
  const list = useRef<HTMLDivElement>(null);
  useEffect(() => {
    if (!open) return;
    list.current?.querySelector<HTMLElement>(`[data-book="${CSS.escape(current.book)}"]`)?.scrollIntoView({ block: "center" });
  }, [open, current.book]);
  const split = index.books.findIndex((b) => b.key === "Matt");
  const groups =
    split > 0
      ? [
          { name: "Old Testament", books: index.books.slice(0, split) },
          { name: "New Testament", books: index.books.slice(split) },
        ]
      : [{ name: "Books", books: index.books }];
  return (
    <Sheet open={open} onClose={onClose} title={entry.shortTitle} size="lg" dismissible className={s.contents}>
      <div ref={list} className={s.contentsList}>
        {groups.map((g) => (
          <section key={g.name} aria-label={g.name}>
            <h3 className={s.group}>{g.name}</h3>
            <ul className={s.books}>
              {g.books.map((b) => {
                const name = bookByOsis(b.key)?.name ?? b.name;
                const here = b.key === current.book;
                return (
                  <li key={b.key} data-book={b.key}>
                    <button type="button" className={s.bookRow} aria-expanded={shown === b.key} data-here={here || undefined} onClick={() => setShown(shown === b.key ? "" : b.key)}>
                      <span>{name}</span>
                      {b.display && <span className={s.display}>{b.display}</span>}
                      <span className={s.count} aria-hidden="true">
                        {b.chapters.length}
                      </span>
                    </button>
                    {shown === b.key && (
                      <ol className={s.chapters} aria-label={`Chapters of ${name}`}>
                        {b.chapters.map((c) => (
                          <li key={c}>
                            <Link
                              to="/bible/$id/$book/$chapter"
                              params={{ id: entry.id, book: b.key, chapter: String(c) }}
                              className={s.chapterLink}
                              aria-current={here && c === current.chapter ? "page" : undefined}
                              onClick={onClose}
                            >
                              {c}
                            </Link>
                          </li>
                        ))}
                      </ol>
                    )}
                  </li>
                );
              })}
            </ul>
          </section>
        ))}
      </div>
    </Sheet>
  );
}

